import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import type { BranchingStory } from '@/types/storytelling'
import { getSessionState, makeChoice } from '@/services/branchingStoryService'

interface BranchingStoryPlayerProps {
  story: BranchingStory
  onComplete: (path: string[]) => void
}

export function BranchingStoryPlayer({ story, onComplete }: BranchingStoryPlayerProps) {
  const [path, setPath] = useState<string[]>([])
  const state = getSessionState(story, path)

  const handleChoice = (choiceId: string) => {
    const nextNodeId = makeChoice(story, state.currentNodeId, choiceId)
    if (!nextNodeId) return
    const newPath = [...path, nextNodeId]
    setPath(newPath)
    const nextState = getSessionState(story, newPath)
    if (nextState.isComplete) onComplete(newPath)
  }

  const handleRestart = () => setPath([])

  return (
    <div className="max-w-lg mx-auto px-5 py-6">
      <AnimatePresence mode="wait">
        <motion.div key={state.currentNodeId} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
          <div className="rounded-2xl p-5 bg-white mb-4" style={{ border: '1px solid var(--color-story-border)' }}>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--color-story-text)' }}>{state.currentNode.content}</p>
          </div>
          {state.isComplete ? (
            <div className="text-center mt-6">
              <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--color-story-primary-light)' }}>
                {state.currentNode.endingType === 'good' ? 'The End ✨' : state.currentNode.endingType === 'twist' ? 'The End... or is it? 🔑' : 'The End'}
              </p>
              <p className="text-sm mb-4" style={{ color: 'var(--color-story-text-muted)' }}>You made {path.length} choices to reach this ending.</p>
              <button onClick={handleRestart} className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white" style={{ background: 'linear-gradient(135deg, var(--color-story-primary), var(--color-story-primary-light))' }}>Try a different path</button>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {state.currentNode.choices?.map((choice) => (
                <button key={choice.id} onClick={() => handleChoice(choice.id)} className="rounded-xl p-4 text-left bg-white transition-all hover:shadow-md" style={{ border: '2px solid var(--color-story-border)' }}>
                  <p className="font-semibold text-sm" style={{ color: 'var(--color-story-text)' }}>{choice.text}</p>
                  <p className="text-xs mt-1" style={{ color: 'var(--color-story-text-muted)' }}>{choice.preview}</p>
                </button>
              ))}
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}
